import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  AlertCardComponent,
  AlertType,
  DialogSkeletonComponent,
  MatButtonLoadingDirective,
  MultiFileUploaderComponent,
  SnackbarService,
  UploadedFileType,
} from '@tiltgroup/web-components';
import { MatButtonModule } from '@angular/material/button';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import { ShipmentsQueriesService } from '@services/queries/services/shipments-queries.service';
import { MatDialogClose, MatDialogRef } from '@angular/material/dialog';

type ImportShipmentForm = {
  documents: FormControl<File[] | null>;
};

@Component({
  selector: 'nx-import-shipment-modal',
  imports: [
    DialogSkeletonComponent,
    MatButtonModule,
    MatButtonLoadingDirective,
    ReactiveFormsModule,
    AlertCardComponent,
    MatDialogClose,
    MultiFileUploaderComponent,
  ],
  templateUrl: './import-shipment-modal.component.html',
  styleUrl: './import-shipment-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportShipmentModalComponent {
  private formBuilder = inject(FormBuilder);
  private queriesService = inject(ShipmentsQueriesService);
  private dialogRef = inject(MatDialogRef);
  private snackbarService = inject(SnackbarService);

  busy = signal<boolean>(false);

  protected readonly AlertType = AlertType;

  form: FormGroup<ImportShipmentForm>;

  acceptedFormats: UploadedFileType[] = [
    UploadedFileType.PDF,
    UploadedFileType.PNG,
    UploadedFileType.JPG_JPEG,
    UploadedFileType.TXT,
    UploadedFileType.CSV,
  ];

  maxFileSize = 1024 * 1024 * 10; // 10 MB in bytes

  constructor() {
    this._createUploadForm();
  }

  importShipment() {
    if (!this.form.valid) {
      return;
    }

    this.busy.set(true);

    const value = this.form.value;

    this.queriesService
      .importShipmentDocuments(value.documents)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (res) => {
          this.dialogRef.close(res);
          this.snackbarService.showNotification(
            value.documents.length === 1
              ? 'Shipment has been imported. It will appear in the list once processing is complete.'
              : 'Shipments have been imported. They will appear in the list once processing is complete.'
          );
        },
        error: () => {
          this.snackbarService.showNotification(
            'Import failed. Please try again.'
          );
        },
      });
  }

  private _createUploadForm() {
    this.form = this.formBuilder.group({
      documents: this.formBuilder.control<File[] | null>(
        null,
        Validators.required
      ),
    });
  }
}
